// src/utils/helpers.ts
// Универсальные утилиты для форматирования, проверки и работы с данными

/**
 * Получает инициалы из имени
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Форматирует дату для отображения
 */
export const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Сегодня';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return 'Завтра';
  } else {
    return date.toLocaleDateString('ru-RU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }
};

/**
 * Форматирует цену для отображения
 */
export const formatPrice = (cost?: number | null): string => {
  if (cost === null || cost === undefined) {
    return 'По запросу';
  }
  return `${cost.toLocaleString('ru-RU')} ₽`;
};

/**
 * Проверяет валидность email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Проверяет валидность номера телефона
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^(\+7|8)?[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Форматирует номер телефона
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('7')) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
  }
  if (cleaned.length === 10) {
    return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8)}`;
  }
  return phone;
};

/**
 * Создает задержку
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Безопасно парсит JSON
 */
export const safeJsonParse = <T>(jsonString: string, fallback: T): T => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return fallback;
  }
};

/**
 * Дебаунс функция
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/* -----------------------------------------------------
 * 💻 ШАГ 1: Timezone в анкету
 * ----------------------------------------------------- */

/**
 * Получает timezone пользователя для отправки на бекенд
 */
export const getUserTimezone = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('📍 Часовой пояс пользователя:', timezone);
    return timezone;
  } catch (error) {
    console.warn('⚠️ Не удалось получить timezone, используем UTC');
    return 'UTC';
  }
};

/**
 * Получает детальную информацию о времени пользователя
 */
export const getUserTimeInfo = () => {
  const timezone = getUserTimezone();
  const now = new Date();

  return {
    timezone,
    currentTime: now.toISOString(),
    localTime: now.toLocaleString('ru-RU', { timeZone: timezone }),
    offsetHours: -now.getTimezoneOffset() / 60,
    displayName: new Intl.DateTimeFormat('ru-RU', {
      timeZoneName: 'long',
      timeZone: timezone
    })
      .format(now)
      .split(', ')[1] // например: "Москва, стандартное время"
  };
};

/**
 * Список популярных таймзон для селектора
 */
export const POPULAR_TIMEZONES =[
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)', offset: 3 },
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)', offset: 2 },
  { value: 'Europe/Samara', label: 'Самара (UTC+4)', offset: 4 },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)', offset: 5 },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)', offset: 6 },
  { value: 'Asia/Novosibirsk', label: 'Новосибирск (UTC+7)', offset: 7 },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)', offset: 8 },
  { value: 'Asia/Yakutsk', label: 'Якутск (UTC+9)', offset: 9 },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)', offset: 10 },
  { value: 'Asia/Magadan', label: 'Магадан (UTC+11)', offset: 11 },
  { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)', offset: 12 },
  { value: 'Europe/Tbilisi', label: 'Тбилиси (UTC+4)', offset: 4 },
  { value: 'Europe/Minsk', label: 'Минск (UTC+3)', offset: 3 },
  { value: 'Asia/Almaty', label: 'Алматы (UTC+6)', offset: 6 },
  { value: 'Asia/Tashkent', label: 'Ташкент (UTC+5)', offset: 5 },
];
